import { useEffect, useState } from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useAuth0User } from '@hooks/useAuth0User';
import { useLoginHref } from '@hooks/useLoginHref';
import { getUserInfo } from '@api-service/account';
import { getUserSubscriptionInfo, isUserAuthenticated } from '@lib/utils';
import { AnalyticsEvents, captureAnalyticsEvent } from '@lib/analytics/events';
import { getAnalyticsMembershipTier } from '@lib/analytics/posthog-super-properties';
import {
  hasInvoiceBackedBilling,
  isPayPalBilling,
  resolveBillingProvider
} from '@components/Account/SubscriptionRow/subscription-display';

import { WEBSITE_NAME } from '@lib/constants';

import Button from '@components/atoms/button';
import { triggerToast } from '@components/atoms/toast';
import { BodyRegular, H1, H2, H4 } from '@components/atoms/typography';
import { PayPalInfo } from '@lib/page-utils/Account';
import MiniLeagueSection from '@components/Account/MiniLeagueSection';
import SubscriptionRow from '@components/Account/SubscriptionRow';
import UpdateCardModal from '@components/Account/UpdateCardModal';
import { useAccessToken } from '@hooks/useAccessToken';

const ACCOUNT_PATH = '/account';

const AccountShell = ({ children }: { children: React.ReactNode }) => (
  <div>
    <Head>
      <title>{WEBSITE_NAME}</title>
    </Head>
    <div className="container mx-auto px-3">
      <div className="relative px-2 pt-10 pb-20 bg-white sm:px-6 lg:px-8">
        <div className="relative mx-auto max-w-7xl">
          <H1 className="mb-3">Account information</H1>
          {children}
        </div>
      </div>
    </div>
  </div>
);

const Account = () => {
  const router = useRouter();
  const { user, isLoading } = useAuth0User();
  const loginHref = useLoginHref(ACCOUNT_PATH);

  useEffect(() => {
    if (!window.churnkey || !window.churnkey.created) {
      window.churnkey = { created: true };
      const a = document.createElement('script');
      a.src = `https://assets.churnkey.co/js/app.js?appId=${process.env.NEXT_PUBLIC_CHURNKEY_APP_ID}`;
      a.async = true;
      a.dataset.cookieyes = 'cookieyes-functional';
      document.head.appendChild(a);
    }
  }, []);

  const [nextPaymentDate, setNextPaymentDate] = useState<string | undefined>(
    ''
  );
  const [subscriptionExpiresOn, setSubscriptionExpiresOn] = useState<
    string | undefined
  >('');
  const [nextPaymentAmount, setNextPaymentAmount] = useState<string>('');
  const [currencyCode, setCurrencyCode] = useState<string>('');
  const [isCancelled, setIsCancelled] = useState<boolean | undefined>(
    undefined
  );
  const [stripeCustomerId, setStripeCustomerId] = useState<string>('');
  const [userHash, setUserHash] = useState<string>('');
  const [isChangePlanOpen, setIsChangePlanOpen] = useState<boolean>(false);
  const [isCancelPlanOpen, setIsCancelPlanOpen] = useState<boolean>(false);
  const [isStripeSubscription, setIsStripeSubscription] =
    useState<boolean>(false);
  const [invoiceProvider, setInvoiceProvider] = useState<string | undefined>(
    undefined
  );
  const [isUpdateCardOpen, setIsUpdateCardOpen] = useState<boolean>(false);
  const { accessToken } = useAccessToken();
  const userSubInfo = getUserSubscriptionInfo(user);
  // The subscription response and the session claim can disagree while tokens
  // refresh, and the invoice on screen belongs to the former, so that wins once
  // it arrives.
  const billingProvider = resolveBillingProvider(userSubInfo, invoiceProvider);

  useEffect(() => {
    if (isUserAuthenticated(user) && accessToken) {
      getUserData();
    }
  }, [user, accessToken]);

  const getUserData = async () => {
    if (!accessToken) return;

    try {
      const result = await getUserInfo({ accessToken });

      // A free account has nothing to populate here, and the plan block reads
      // from the session claim instead. Every piece of state below already
      // defaults to the free-account view, so leaving it untouched is correct.
      if (!result.hasSubscription) {
        setIsStripeSubscription(false);
        return;
      }

      const { invoice, customerId, userHash, provider } = result.subscription;

      setIsCancelled(invoice?.isCancelled);
      setNextPaymentAmount(invoice?.nextPaymentAmount ?? '');
      setNextPaymentDate(invoice?.nextPaymentDate ?? undefined);
      setSubscriptionExpiresOn(invoice?.subscriptionExpiresOn ?? undefined);
      setStripeCustomerId(customerId ?? '');
      setCurrencyCode(invoice?.currency ?? '');
      setUserHash(userHash ?? '');
      setInvoiceProvider(provider ?? undefined);
      setIsStripeSubscription(hasInvoiceBackedBilling(provider));
    } catch (error) {
      console.error('Error fetching user data:', error);
    }
  };

  const initChurnkey = () => {
    setIsChangePlanOpen(false);
    if (stripeCustomerId && userHash) {
      // FFH-2372: the cancel flow OPENING is the intent signal; onCancel
      // below is completion, a different thing. tenure_days stays null until
      // the account API exposes a subscription start date — nothing
      // client-side knows when the subscription began.
      captureAnalyticsEvent(AnalyticsEvents.subscriptionCancelStarted, {
        tier: getAnalyticsMembershipTier(user),
        tenure_days: null
      });
      window.churnkey?.init('show', {
        subscriptionId: userSubInfo?.source?.id,
        customerId: stripeCustomerId,
        authHash: userHash,
        appId: process.env.NEXT_PUBLIC_CHURNKEY_APP_ID,
        mode: process.env.NEXT_PUBLIC_CHURNKEY_ENV,
        provider: process.env.NEXT_PUBLIC_CHURNKEY_PAYMENT_PROVIDER,
        handleSupportRequest: () => {
          router.push('/contact');
          window.churnkey.hide();
        },
        onCancel: async () => {
          window.churnkey.hide();
          triggerToast({
            title: 'Subscription cancelled',
            subTitle: 'Subscription has been cancelled successfully.',
            variant: 'success'
          });
          if (!accessToken) return;
          try {
            const result = await getUserInfo({ accessToken });
            if (!result.hasSubscription) return;

            const { invoice } = result.subscription;
            setIsCancelled(invoice?.isCancelled);
            setSubscriptionExpiresOn(
              invoice?.subscriptionExpiresOn ?? undefined
            );
          } catch (error) {
            // The cancellation itself succeeded and the toast has already
            // fired, so a failure to re-read the subscription must not surface
            // as an unhandled rejection inside the Churnkey callback.
            console.error(
              'Error refreshing subscription after cancellation:',
              error
            );
          }
        }
      });
    }
  };

  // The page reads entirely from the session, so it has nothing honest to show
  // until the session settles, and nothing at all to show without one. Rendering
  // the plan block regardless left a signed-out visitor looking at empty fields
  // and a "Loading..." status that never resolved (FFH-2453).
  if (isLoading) {
    return (
      <AccountShell>
        <BodyRegular>Loading your account details...</BodyRegular>
      </AccountShell>
    );
  }

  if (!isUserAuthenticated(user)) {
    return (
      <AccountShell>
        <BodyRegular className="mb-5">
          Sign in to see your membership and subscription details.
        </BodyRegular>
        <Button href={loginHref} variant="primary">
          Sign in
        </Button>
      </AccountShell>
    );
  }

  return (
    <div>
      <Head>
        <title>{WEBSITE_NAME}</title>
      </Head>
      <div className="container mx-auto px-3">
        <div
          className={`flex flex-col lg:grid 'lg:grid-cols-[minmax(0,1fr)_350px] xl:grid-cols-[minmax(0,1fr)_350px] gap-2 md:gap-8`}
        >
          <div className="relative px-2 pt-10 mb-0 md:pb-20 bg-white sm:px-6 lg:pb-10 lg:px-8">
            <div className="relative mx-auto max-w-7xl">
              <H1>Account information</H1>
              {isPayPalBilling(billingProvider) && <PayPalInfo />}
              <div className="mb-5 md:mb-12">
                <div className="flex gap-4 mb-4">
                  <div className="mb-4 min-w-[300px]">
                    <H4 className="mt-1">Email</H4>
                    <BodyRegular className="mt-1">{user?.email}</BodyRegular>
                  </div>
                </div>
              </div>
              <H1>Your plan</H1>
              <div className="mb-5 md:mb-12">
                <SubscriptionRow
                  setIsChangePlanOpen={setIsChangePlanOpen}
                  nextPaymentDate={nextPaymentDate}
                  nextPaymentAmount={nextPaymentAmount}
                  subscriptionExpiresOn={subscriptionExpiresOn}
                  isChangePlanOpen={isChangePlanOpen}
                  setIsCancelPlanOpen={setIsCancelPlanOpen}
                  initChurnkey={initChurnkey}
                  isCancelPlanOpen={isCancelPlanOpen}
                  stripeCustomerId={stripeCustomerId}
                  userSubInfo={userSubInfo}
                  user={user}
                  isCancelled={isCancelled}
                  currencyCode={currencyCode}
                  billingProvider={billingProvider}
                />
              </div>
              {isStripeSubscription && (
                <>
                  <H2>Manage billing</H2>
                  <div className="flex mt-5">
                    <Button
                      variant="secondary"
                      onClick={() => setIsUpdateCardOpen(true)}
                    >
                      Update card
                    </Button>
                  </div>
                  <UpdateCardModal
                    isOpen={isUpdateCardOpen}
                    accessToken={accessToken}
                    onClose={() => setIsUpdateCardOpen(false)}
                    onCardUpdated={getUserData}
                  />
                </>
              )}
              <MiniLeagueSection />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Account;
